/*
 * App Router version of `../createOrderHandler`.
 *
 * Same flow and the same business rules, answering with a `NextResponse`
 * instead of writing to a pages-router `res`, so `create-order` no longer needs
 * `withPagesApiCompat`. The legacy folder is left in place and still working:
 * the invoice handlers share its Ingrid/nShift session helpers and are still
 * served through the shim. It goes away once every order handler has a copy
 * here.
 */
import { NextResponse, type NextRequest } from 'next/server'
import storefrontApi from 'api/storefront-api'
import { getCountryCodeFromPaymentConfig } from 'api/utils/payment/getCountryCodeFromPaymentConfig'
import { CustomerType, type CustomerDTO, type Language, type Option } from 'ecosystem'
import { buyerVatRatesFor, createLogger, DEFAULT_COUNTRY } from 'shared-utils'
import { setRequestLocale } from '../../../common/api/setRequestLocale'
import { routeHandlerWithLogging } from '../../../logging/routeHandlerWithLogging'
import { parseCreateOrderRequest } from './schema'
import { type CreateOrderContext, type CreateOrderHandlerOpts } from './types'
import { klarnaPaymentProvider } from './klarnaPaymentProvider'
import { nexiPaymentProvider } from './nexiPaymentProvider'
import { sveaPaymentProvider } from './sveaPaymentProvider'
import { qliroPaymentProvider } from './qliroPaymentProvider'
import { briqpayPaymentProvider } from './briqpayPaymentProvider'

const logger = createLogger('createOrderRouteHandler')

interface ResolvedBuyer extends Pick<
  CreateOrderHandlerOpts,
  'buyerVat' | 'customerType' | 'customerId'
> {
  /** The record the three fields above were derived from, kept so Svea can
   *  prefill its session from it instead of fetching the same customer twice. */
  customer: Option<CustomerDTO>
}

/**
 * Resolve the buyer's VAT treatment and business/consumer classification once
 * per request, so every provider branch prices service and shipping rows on the
 * same basis the cart lines were built on. A lookup failure must not block
 * checkout — we fall back to the store's own rate, which is what happens today,
 * and leave `customerType` unknown so no delivery rule keys off a guess.
 *
 * The same lookup decides whether `customerId` is safe to stamp on the order.
 * The BE rejects a create whose `customerId` does not resolve to an *active*
 * Customer in the store, and a rejected create is a broken checkout — so the id
 * is carried forward only when this lookup proves it good. Anything else (an
 * unknown id, a deactivated account, a failed call) falls back to the
 * pre-existing guest behaviour: the order links by billing email at
 * provisioning instead.
 */
const resolveBuyer = async (opts: CreateOrderHandlerOpts): Promise<ResolvedBuyer> => {
  // An anonymous shopper is a consumer by definition.
  if (!opts.customerId) {
    return {
      buyerVat: null,
      customerType: CustomerType.CONSUMER,
      customerId: null,
      customer: null
    }
  }

  try {
    const customer = await storefrontApi.customer.findById(opts.customerId)
    const tenantCountry =
      getCountryCodeFromPaymentConfig(opts.paymentProviderConfig) ?? DEFAULT_COUNTRY

    if (!customer?.active) {
      logger.error(
        `Session customer ${opts.customerId} is not active; placing the order without an identity`
      )
    }

    return {
      buyerVat: buyerVatRatesFor(customer?.companyProfile, tenantCountry),
      customerType: customer?.type,
      customerId: customer?.active ? customer.id : null,
      customer: customer ?? null
    }
  } catch (e) {
    logger.error('Failed to resolve the buyer; falling back to store rate', e)
    return { buyerVat: null, customerId: null, customer: null }
  }
}

export const createOrderRouteHandler = (opts: CreateOrderHandlerOpts) =>
  routeHandlerWithLogging(async function POST(request: NextRequest) {
    // Installs the market's api credentials from the shopper's cookie. The
    // credentials locale always follows the cookie; the display locale below
    // may differ, because a store can pin its checkout to one language.
    const credentialsLocale = setRequestLocale(request)

    const { payload, error: invalidRequest } = await parseCreateOrderRequest(request)

    if (!payload) {
      return NextResponse.json({ error: invalidRequest }, { status: 400 })
    }

    // Resolved once, after the body is known to be good, so a malformed request
    // never costs a customer look-up.
    const { customer, ...buyer } = await resolveBuyer(opts)
    const optsWithBuyer: CreateOrderHandlerOpts = { ...opts, ...buyer }

    const ctx: CreateOrderContext = {
      payload,
      locale: (opts.locale || credentialsLocale) as Language,
      customer
    }

    try {
      switch (opts.paymentProviderConfig?.providerName ?? 'KLARNA') {
        case 'KLARNA':
          return await klarnaPaymentProvider(ctx, optsWithBuyer)
        case 'NEXI':
          return await nexiPaymentProvider(ctx, optsWithBuyer)
        case 'SVEA':
          return await sveaPaymentProvider(ctx, optsWithBuyer)
        case 'QLIRO':
          return await qliroPaymentProvider(ctx, optsWithBuyer)
        case 'BRIQPAY':
          return await briqpayPaymentProvider(ctx, optsWithBuyer)
        // todo: add LEDYER
        case 'LEDYER': {
          throw new Error('Not implemented yet: "LEDYER" case')
        }
      }
    } catch (e) {
      return NextResponse.json(
        { error: e instanceof Error ? e.message : 'Unknown error' },
        { status: 400 }
      )
    }

    // Unreachable while the switch above covers every `PaymentProviderName` —
    // kept so an added provider answers with an error instead of an empty 200.
    return NextResponse.json({ error: 'Unsupported payment provider.' }, { status: 400 })
  })
